export class PublicationsOverview extends HTMLElement {
    private items: any[] = []
    private hasLoaded = false
    private hasMore = false
    private offset = 0
    private activeTagIds = new Set<string>()
    private isLoading = false

    private readonly PAGE_SIZE = 10
    private readonly NO_RESULTS_TEXT_ATTR = 'data-no-results-text'
    private readonly MORE_LINK_TEXT_ATTR = 'data-more-link-text'
    private readonly MORE_LINK_ARIA_LABEL_TEMPLATE_ATTR = 'data-more-link-aria-label-template'
    private readonly REFERENCE_LINK_TEXT_ATTR = 'data-reference-link-text'
    private readonly LOAD_MORE_TEXT_ATTR = 'data-load-more-text'

    connectedCallback() {
        this.bindFilterButtons()
        this.fetchEntries(false)
    }

    fetchEntries(append: boolean) {
        if (this.isLoading) return
        this.isLoading = true

        const list = this.querySelector('.list')
        if (list) list.setAttribute('aria-busy', 'true')

        if (!append) {
            this.offset = 0
            this.items = []
        }

        const payload: any = {
            section: 'publications',
            limit: this.PAGE_SIZE,
            offset: this.offset,
        }

        if (this.activeTagIds.size > 0) {
            payload.categories = { tags: Array.from(this.activeTagIds) }
        }

        fetch('/api/entries', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then(async (response) => {
                this.hasLoaded = true
                this.isLoading = false
                const text = await response.text()
                let result: any
                try {
                    result = JSON.parse(text)
                } catch {
                    this.hasMore = false
                    this.renderResult([], append)
                    return
                }
                const entries = result?.items
                if (!Array.isArray(entries)) {
                    this.hasMore = false
                    this.renderResult([], append)
                    return
                }
                const mapLink = (link: any) =>
                    link
                        ? {
                              url: link.url || '',
                              label: link.label || '',
                              customLabel: link.customLabel || '',
                              ariaLabel: link.ariaLabel || '',
                              type: link.type || 'url',
                          }
                        : null
                const newItems = entries.map((entry: any) => ({
                    title: entry.title || '',
                    date: entry.date || '',
                    tags: Array.isArray(entry.tags) ? entry.tags : [],
                    text: entry.teaserText || entry.mehrzeiligerText || '',
                    ctaLink: mapLink(entry.ctaLink),
                    reference: mapLink(entry.reference),
                }))
                this.items = append ? [...this.items, ...newItems] : newItems
                this.offset = this.items.length
                this.hasMore = result.hasMore === true
                this.renderResult(newItems, append)
            })
            .catch(() => {
                this.isLoading = false
                this.hasMore = false
                this.renderResult([], append)
            })
    }

    private formatDateForDisplay(dateStr: string): string {
        if (!dateStr) return ''
        const parts = dateStr.split('-')
        if (parts.length !== 3) return dateStr
        const [y, m, d] = parts.map(Number)
        return `${y}`
    }

    private getMoreLinkAriaLabel(title: string, ctaAriaLabel: string, template: string): string {
        if (ctaAriaLabel) return ctaAriaLabel
        return template.replace('{title}', title)
    }

    private bindFilterButtons() {
        this.querySelectorAll('.tag-filter-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const tagId = (btn as HTMLElement).dataset.tagId ?? ''
                this.activeTagIds.clear()
                if (tagId !== '') {
                    this.activeTagIds.add(tagId)
                }
                this.updateFilterButtonStates()
                this.fetchEntries(false)
            })
        })
    }

    private updateFilterButtonStates() {
        this.querySelectorAll('.tag-filter-btn').forEach((btn) => {
            const tagId = (btn as HTMLElement).dataset.tagId ?? ''
            const isActive = tagId === '' ? this.activeTagIds.size === 0 : this.activeTagIds.has(tagId)
            btn.classList.toggle('is-active', isActive)
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
        })
    }

    private renderLinkButton(link: any, label: string, ariaLabel: string): string {
        const isInternal = ['entry', 'category'].includes(link.type)
        const isAsset = link.type === 'asset'
        const icon = isInternal ? 'internal-icon' : isAsset ? 'download-icon' : 'external-icon'
        const target = isInternal ? '' : 'target="_blank" rel="noopener"'
        return `<a href="${link.url}" class="button text-bodysmall" ${target} aria-label="${ariaLabel}">
            <b class="text-bodysmall">${label}</b>
            <i class="small-icon ${icon}"></i>
        </a>`
    }

    private renderItem(item: any, moreLinkText: string, moreLinkAriaLabelTemplate: string, referenceLinkText: string): string {
        const hasLinks = item.reference || item.ctaLink
        const linksHtml = hasLinks
            ? `
            <div class="oi-buttons">
                ${item.reference ? this.renderLinkButton(item.reference, item.reference.customLabel || referenceLinkText, item.reference.ariaLabel || item.reference.customLabel || referenceLinkText) : ''}
                ${item.ctaLink ? this.renderLinkButton(item.ctaLink, item.ctaLink.customLabel || moreLinkText, this.getMoreLinkAriaLabel(item.title, item.ctaLink.ariaLabel ?? '', moreLinkAriaLabelTemplate)) : ''}
            </div>
        `
            : ''
        return `
            <li class="border-t border-darkgrey pt-listPt last:mb-0 mb-listMb">
                <div class="flex flex-col md:flex-row justify-between items-start gap-x-6 gap-y-3">
                    <div class="flex-1 min-w-0">
                        <div class="text-date">${this.formatDateForDisplay(item.date)}</div>
                    </div>
                    ${
                        item.tags.length > 0
                            ? `
                        <div class="oi-tags shrink-0 pt-1" aria-label="${item.tags.map((t: any) => t.title).join(', ')}">
                            ${item.tags
                                .map(
                                    (tag: any) => `
                                <span class="oi-tag" aria-hidden="true">${tag.title}</span>
                            `,
                                )
                                .join('')}
                        </div>
                    `
                            : ''
                    }
                </div>
                <div class="oi-richtext mt-listMt">
                    <h2 class="font-sans font-bold text-base !mb-0">${item.title}</h2>
                    ${item.text ? `<p class="text-listtext whitespace-pre-line !my-0 !max-w-full pt-textGap">${item.text}</p>` : ''}
                    <div class="mt-listMt">
                        ${linksHtml}
                    </div>
                </div>
            </li>
        `
    }

    private renderResult(newItems: any[], append: boolean) {
        const list = this.querySelector('.list')
        const loadMoreContainer = this.querySelector('.load-more-container')
        if (!list) return

        list.setAttribute('aria-busy', 'false')

        const noResultsText = this.getAttribute(this.NO_RESULTS_TEXT_ATTR) ?? 'Keine Publikationen gefunden.'
        const moreLinkText = this.getAttribute(this.MORE_LINK_TEXT_ATTR) ?? 'Mehr erfahren'
        const moreLinkAriaLabelTemplate = this.getAttribute(this.MORE_LINK_ARIA_LABEL_TEMPLATE_ATTR) ?? 'Mehr erfahren zu {title}'
        const referenceLinkText = this.getAttribute(this.REFERENCE_LINK_TEXT_ATTR) ?? 'Referenz herunterladen'
        const loadMoreText = this.getAttribute(this.LOAD_MORE_TEXT_ATTR) ?? 'Mehr anzeigen'

        if (!append) {
            if (this.items.length > 0) {
                list.innerHTML = this.items
                    .map((item) => this.renderItem(item, moreLinkText, moreLinkAriaLabelTemplate, referenceLinkText))
                    .join('')
            } else if (this.hasLoaded) {
                list.innerHTML = `<li class="py-8 text-base">${noResultsText}</li>`
            } else {
                list.innerHTML = ''
            }
        } else if (newItems.length > 0) {
            list.insertAdjacentHTML(
                'beforeend',
                newItems.map((item) => this.renderItem(item, moreLinkText, moreLinkAriaLabelTemplate, referenceLinkText)).join(''),
            )
        }

        if (loadMoreContainer) {
            if (this.hasMore) {
                loadMoreContainer.innerHTML = `
                    <button type="button" class="load-more-btn group mx-auto flex flex-col items-center cursor-pointer text-bodysmall text-darkgrey">
                        <span class="text-loadmore">${loadMoreText}</span>
                        <span class="flex items-center mt-listMt justify-center rounded-full border border-darkgrey py-[var(--sp-loadmore-py)] px-[var(--sp-loadmore-px)] transition-colors duration-200 group-hover:bg-green group-hover:border-green">
                            <svg width="24" height="24" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg" class="rotate-90 transition-transform duration-500 group-hover:translate-y-1" aria-hidden="true">
                                <path d="M15.0252 23.6878L26.6924 12.0205L15.0252 0.353243M26.6924 12.0205L0 12.0205" stroke="currentColor"/>
                            </svg>
                        </span>
                    </button>
                `
                loadMoreContainer.querySelector('button')?.addEventListener('click', () => {
                    this.fetchEntries(true)
                })
            } else {
                loadMoreContainer.innerHTML = ''
            }
        }
    }
}
